import * as Yup from 'yup';

import {
  Function,
  LMC_ACCEPTED_APPLICANT_AGE,
  LMC_ACCEPTED_APPLICANT_AGE_START,
} from 'shared/interface/globalInterface';
import { namesPattern, onlyDigits, onlyLetters, sortCode, ukPostalCode } from 'shared/regex/regex';
import { calculateDiff } from 'utils/date';
import { Nullable } from 'utils/types';

export const telephoneValidation = (fieldName: string = 'telephone'): Yup.StringSchema<string> =>
  Yup.string()
    .matches(/^[0]+[0-9]+$/, `Please enter a valid ${fieldName.toLowerCase()} number.`)
    .test('len', `Please enter a valid ${fieldName.toLowerCase()} number.`, (val) => {
      return val ? val.length > 10 : true;
    });

export const postcodeValidation = (fieldLabel: string = 'Post Code'): Yup.StringSchema<string> => {
  return Yup.string().matches(ukPostalCode, {
    excludeEmptyString: true,
    message: `${fieldLabel} is invalid.`,
  });
};

export const checkAgeError = (value: string) => {
  const ageInMonth = calculateDiff(value, 'years', true);
  return ageInMonth >= LMC_ACCEPTED_APPLICANT_AGE_START && ageInMonth <= LMC_ACCEPTED_APPLICANT_AGE;
};

export const nullableString = () => Yup.string().nullable();

export const nullableNumber = () =>
  Yup.number()
    .nullable()
    .max(2000000000, 'Must be less than 2.000.000.000')
    .typeError('Must be a number.');

export const positiveCurrencyIncludingZeroValidation = (): Yup.NumberSchema<Nullable<number>> =>
  nullableNumber().min(0, 'Must be at least 0');

export const positiveCurrencyValidation = (): Yup.NumberSchema<Nullable<number>> =>
  nullableNumber().positive('Must be at least 1');

export const percentageValidation = (
  min: number = 0,
  max: number = 100,
  message: Nullable<string> = null,
  nullable: boolean = false,
): Yup.NumberSchema<Nullable<number>> =>
  Yup.number()
    .test('is-percentage', message ? message : `Value must be between ${min}% - ${max}%.`, (value) => {
      if (nullable && (value === '' || value === null || value === undefined)) {
        return true;
      }
      return isNaN(value) || (value >= min && value <= max);
    })
    .typeError('Must be a number.');

export const optionalNumberValidationSchema = (
  when: string | string[],
  is: string | number | boolean | Function<boolean>,
  thenSchema?: Yup.NumberSchema<Nullable<number>>,
  nonRequired?: boolean,
  otherwise?: Yup.NumberSchema<Nullable<number>>,
): Yup.NumberSchema<Nullable<number>> =>
  Yup.number().when(when, {
    is,
    then: () => {
      let optionalNumberSchema;
      if (nonRequired) {
        optionalNumberSchema = nullableNumber()
          .typeError('This field is required.')
          .transform((val) => (isNaN(val) ? null : val));
      } else {
        optionalNumberSchema = nullableNumber()
          .typeError('This field is required.')
          .required('This field is required.')
          .transform((val) => (isNaN(val) ? null : val));
      }

      return thenSchema ? optionalNumberSchema.concat(thenSchema) : optionalNumberSchema;
    },
    otherwise: otherwise ? otherwise : nullableNumber().transform((val) => (isNaN(val) ? null : val)),
  });

export const optionalStringValidationSchema = (
  when: string | string[],
  is: string | number | boolean | Function<boolean>,
  thenSchema?: Yup.StringSchema<Nullable<string>>,
  nonRequired?: boolean,
): Yup.StringSchema<Nullable<string>> =>
  Yup.string().when(when, {
    is,
    then: () => {
      let optionalStringSchema;
      if (nonRequired) {
        optionalStringSchema = nullableString().typeError('This field is required.');
      } else {
        optionalStringSchema = nullableString()
          .required('This field is required.')
          .typeError('This field is required.');
      }

      return thenSchema ? optionalStringSchema.concat(thenSchema) : optionalStringSchema;
    },
    otherwise: nullableString(),
  });

export const humanAnyNameValidation = (fieldName: string = 'This field'): Yup.StringSchema<string> =>
  Yup.string().matches(namesPattern, `${fieldName} is invalid.`);

export const emailValidation = (fieldName: string = 'Email'): Yup.StringSchema<string> =>
  Yup.string()
    .typeError(`${fieldName} is invalid.`)
    .email(`${fieldName} is invalid.`);

export const StrictEmailValidation = (): Yup.StringSchema<string> =>
  // stricter email validation for when the email address is also an id/username field. This should match the
  // corresponding backend validation.
  Yup.string().matches(/^[A-Za-z0-9._+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/, `Please enter a valid email.`);

export const sortCodeValidation = () => Yup.string().matches(sortCode, 'Sort Code must be exactly 6 digits.');

export const accountNumberValidation = () =>
  Yup.string()
    .min(8, 'Account Number requires a minimum of 8 numbers')
    .matches(onlyDigits, 'Account Number requires digits only.');

export const accountNameValidation = () => Yup.string().matches(onlyLetters, 'Account Name requires letters only.');

export const positiveNumber = () => Yup.number().min(1, 'Must be at least 1');

export const retirementAge = (age: Nullable<number>, fieldName: string = 'Retirement age', nullable: boolean = false) =>
  nullableNumber()
    .test('isOldEnough', 'Retirement age should be 55 or more', (value) => {
      if (nullable && !value && value !== 0) {
        return true;
      }
      return value >= 55;
    })
    .test('validRetirementAge', 'Retirement age should be greater than current age', (value) => {
      if (!age || (nullable && !value && value !== 0)) return true;

      return value > age;
    });
